import { Box, Tab, Tabs } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import ApiService from '../../ApiService';
import { DealerPolkAudience } from '../../types/DealerPolkAudience';
import Heading, { SubHeading } from '../../components/Heading';
import PolkAudienceDealerGrid from './PolkAudienceDealerGrid';
import PolkAudienceQueueGrid from './PolkAudienceQueueGrid';
import usePageTitle from '../../hooks/usePageTitle';

import TabPanel from '../../components/TabPanel';
import { useNavigate, useParams } from 'react-router-dom';

const TAB_CONFIG = {
    completed: { label: 'Completed', slug: 'completed' },
    processing: { label: 'Processed', slug: 'processing' },
    queued: { label: 'Queued', slug: 'queued' },
} as const;

type TabSlug = keyof typeof TAB_CONFIG;

function isTabSlug(value: string): value is TabSlug {
    return value in TAB_CONFIG;
}

const DEFAULT_TAB_ID = TAB_CONFIG['completed'].slug;

export default function PolkAudiencesPage() {
    const { tab: tabParameter } = useParams();
    const navigate = useNavigate();
    const [dealers, setDealers] = useState<DealerPolkAudience[] | null>(null);
    const [polkAudienceQueues, setPolkAudienceQueues] = useState<DealerPolkAudience[] | null>(null);
    const initialTabId = tabParameter && isTabSlug(tabParameter) ? tabParameter : DEFAULT_TAB_ID;
    const [selectedTab, setSelectedTab] = useState<TabSlug>(initialTabId);

    useEffect(() => {
        if (!tabParameter) return;

        if (!isTabSlug(tabParameter)) {
            navigate(`/polkAudiences/${DEFAULT_TAB_ID}`, { replace: true });
        } else {
            setSelectedTab(tabParameter);
        }
    }, [tabParameter, navigate]);

    // build & render title
    const activeTabLabel = TAB_CONFIG[selectedTab].label;
    usePageTitle({ segments: [activeTabLabel, 'Polk Audience Monitor'] });

    const completedDealers = useMemo(() => {
        return dealers === null
            ? []
            : dealers.filter((_dealer) => {
                  return getDealerPolkAudienceStatus(_dealer) === 'Completed';
              });
    }, [dealers]);

    const queuedDealers = useMemo(() => {
        return dealers === null
            ? []
            : dealers.filter((_dealer) => {
                  return getDealerPolkAudienceStatus(_dealer) === 'Queued';
              });
    }, [dealers]);

    function getDealerPolkAudienceStatus(dealerPolkAudience: DealerPolkAudience) {
        let status: 'Queued' | 'Processing' | 'Completed';

        if (
            dealerPolkAudience?.hasAllNewCars === 'Yes' &&
            dealerPolkAudience?.hasAllUsedCars === 'Yes' &&
            dealerPolkAudience?.hasAllEVCars === 'Yes'
        ) {
            status = 'Completed';
        } else if (
            dealerPolkAudience?.hasAllNewCars === 'No' ||
            dealerPolkAudience?.hasAllUsedCars === 'No' ||
            dealerPolkAudience?.hasAllEVCars === 'No'
        ) {
            status = 'Queued';
        } else {
            status = 'Processing';
        }

        return status;
    }

    useEffect(() => {
        ApiService.getDealerPolkAudienceData()
            .then(function (response) {
                return ApiService.getDealerPolkAudienceQueueData()
                    .then(function (polkAudienceQueueResponse) {
                        polkAudienceQueueResponse.data.forEach((polkAudienceQueue: DealerPolkAudience) => {
                            for (let index: number = 0; index < response.data.length; index++) {
                                const polkAudienceDealer: DealerPolkAudience = response.data[index];

                                if (!('totalAudiences' in polkAudienceDealer)) {
                                    polkAudienceDealer.totalAudiences = undefined;
                                }

                                if (!('priorityOrder' in polkAudienceDealer)) {
                                    polkAudienceDealer.priorityOrder = undefined;
                                }

                                if (polkAudienceQueue.id === polkAudienceDealer.id) {
                                    polkAudienceDealer.totalAudiences = polkAudienceQueue.totalAudiences;
                                    polkAudienceDealer.priorityOrder = polkAudienceQueue.priorityOrder;
                                    break;
                                }

                                response.data[index] = polkAudienceDealer;
                            }
                        });

                        setDealers(response.data);
                        setPolkAudienceQueues(polkAudienceQueueResponse.data);
                    })
                    .catch(() => {
                        setDealers([]);
                        setPolkAudienceQueues([]);
                    });
            })
            .catch(() => {
                setDealers([]);
                setPolkAudienceQueues([]);
            });
    }, []);

    return (
        <Box
            sx={{
                height: '100%',
                display: 'flex',
                flexDirection: 'column',
            }}
        >
            <Heading>Polk Audience Monitor</Heading>
            <SubHeading>
                Audiences in Completed have been loaded into the Amazon DSP. Audiences in Processing are loading based
                on the Priority Order (higher numbers are first). Audiences in Queued are on deck to be processed next.
            </SubHeading>

            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                <Tabs
                    value={selectedTab}
                    onChange={(_, slugFromTab) => {
                        setSelectedTab(slugFromTab);
                        navigate(`/polkAudiences/${slugFromTab}`, { replace: false });
                    }}
                >
                    <Tab value={TAB_CONFIG['completed'].slug} label={TAB_CONFIG['completed'].label} />
                    <Tab value={TAB_CONFIG['processing'].slug} label={TAB_CONFIG['processing'].label} />
                    <Tab value={TAB_CONFIG['queued'].slug} label={TAB_CONFIG['queued'].label} />
                </Tabs>
            </Box>

            <TabPanel index={TAB_CONFIG['completed'].slug} value={selectedTab}>
                <Box
                    sx={{
                        height: '100vh',
                        width: '100%',
                        flexGrow: 1,
                    }}
                >
                    <PolkAudienceDealerGrid dealers={completedDealers} />
                </Box>
            </TabPanel>

            <TabPanel index={TAB_CONFIG['processing'].slug} value={selectedTab}>
                <Box
                    sx={{
                        height: '100vh',
                        width: '100%',
                        flexGrow: 1,
                    }}
                >
                    <PolkAudienceQueueGrid queues={polkAudienceQueues} />
                </Box>
            </TabPanel>

            <TabPanel index={TAB_CONFIG['queued'].slug} value={selectedTab}>
                <Box
                    sx={{
                        height: '100vh',
                        width: '100%',
                        flexGrow: 1,
                    }}
                >
                    <PolkAudienceDealerGrid dealers={queuedDealers} />
                </Box>
            </TabPanel>
        </Box>
    );
}
